/**
 * BathBreakdownBadge — visual + accessible bath breakdown for buyer surfaces.
 *
 * Reads `{full, threeQuarter, half}` and renders the omitted-zero compact
 * form: `2 full · 1 ¾ · 1 ½`. Falls back to a neutral "Breakdown not provided"
 * Badge when no breakdown is present (legacy listings).
 *
 * Used by:
 *  - Buyer result card (`HousesComponent`) — replaces the float "3.25 Baths".
 *  - Buyer property detail (`HouseDetailPage`) — swaps the stats-strip value.
 *
 * Props:
 *  - full, threeQuarter, half: number | null | undefined
 *  - fallbackTotal: number | null — the legacy `bathrooms` float to render
 *      alongside the "Breakdown not provided" tag when the breakdown is
 *      missing. When null/undefined the tag stands alone.
 *  - size: 'sm' | 'md' (default 'md')
 *  - testId: optional data-testid override
 */

import React from 'react';
import Badge from './Badge';
import './BathBreakdownBadge.css';

const hasValidBreakdown = (full, threeQuarter, half) => {
  // Require all three to be numeric ints (not partial — partial is treated
  // as legacy per design spec §4.4 to avoid misleading buyers).
  return [full, threeQuarter, half].every(
    (v) => typeof v === 'number' && Number.isInteger(v) && v >= 0,
  );
};

const formatSegments = (full, threeQuarter, half) => {
  // Omitted-zero rule per design §4.3.
  const parts = [];
  if (full > 0) parts.push({ label: `${full} full`, sr: `${full} full` });
  if (threeQuarter > 0) {
    parts.push({
      label: `${threeQuarter} ¾`,
      sr: `${threeQuarter} three-quarter`,
    });
  }
  if (half > 0) parts.push({ label: `${half} ½`, sr: `${half} half` });
  return parts;
};

export default function BathBreakdownBadge({
  full,
  threeQuarter,
  half,
  fallbackTotal = null,
  size = 'md',
  testId = 'bath-breakdown-badge',
}) {
  const breakdownOk = hasValidBreakdown(full, threeQuarter, half);

  if (!breakdownOk) {
    return (
      <span
        className="bath-breakdown-badge bath-breakdown-badge--legacy"
        data-testid={testId}
      >
        {fallbackTotal != null && (
          <span
            className="bath-breakdown-badge__legacy-total"
            aria-label={`${fallbackTotal} Baths`}
          >
            <strong>{fallbackTotal}</strong> Baths
          </span>
        )}
        <Badge
          variant="neutral"
          value="legacy"
          size={size === 'sm' ? 'sm' : 'md'}
        >
          <span aria-label="Bathroom breakdown not provided">
            Breakdown not provided
          </span>
        </Badge>
      </span>
    );
  }

  const segments = formatSegments(full, threeQuarter, half);

  if (segments.length === 0) {
    // Explicit 0-bath listing — render a single segment so buyers still see
    // *something*. Sellers confirmed 0 in the editor (see BathBreakdownEditor
    // zero-bath checkbox).
    return (
      <span
        className="bath-breakdown-badge bath-breakdown-badge--zero"
        aria-label="0 bathrooms"
        data-testid={testId}
      >
        <strong>0</strong> Baths
      </span>
    );
  }

  const srSummary = `Bathrooms: ${segments.map((s) => s.sr).join(', ')}`;

  return (
    <span
      className={`bath-breakdown-badge bath-breakdown-badge--${size}`}
      aria-label={srSummary}
      data-testid={testId}
    >
      {segments.map((s, i) => (
        <React.Fragment key={s.label}>
          {i > 0 && (
            <span className="bath-breakdown-badge__sep" aria-hidden="true">·</span>
          )}
          <span className="bath-breakdown-badge__seg" aria-hidden="true">
            {s.label}
          </span>
        </React.Fragment>
      ))}
    </span>
  );
}
